/**
 * mLearning — Cầu nối "Nộp bài" giữa LMS (Angular) và H5P.
 * =========================================================
 *
 * Hoàn toàn CỘNG THÊM: chỉ đăng ký một listener 'message' mới và trả lời bằng
 * một payload key mới ('h5p-score'). Không đụng vào luồng xAPI hiện có.
 *
 * LUỒNG
 *   LMS    --postMessage {type:'layDiemH5P', contentId}-->  client này
 *   client --postMessage {type:'callParentFunction',
 *                         payload:{key:'h5p-score', ...}}-->  LMS
 */

export interface DiemH5P {
  key: 'h5p-score';
  ok: boolean;
  lyDo?: string;
  contentId?: string | number;
  library?: string | null;
  score?: number;
  maxScore?: number;
  tongTuongTac?: number | null;
  daLamTuongTac?: number | null;
  xapi?: any;
}

/**
 * Tìm instance H5P gốc.
 *
 * Ưu tiên `h5pInstance` mà H5PPlayerUI phơi ra sẵn (API chính thức của
 * @lumieducation/h5p-react từ bản 9). Nếu vì lý do nào đó chưa có thì mới
 * mò qua DOM: nội dung chạy trong iframe `#h5p-iframe-<contentId>`.
 */
function timInstance(layInstance?: () => any, contentId?: string | number): any {
  try {
    const i = layInstance ? layInstance() : undefined;
    if (i) { return i; }
  } catch (e) { /* rơi xuống cách dự phòng */ }

  try {
    let frame: HTMLIFrameElement | null = contentId
      ? (document.getElementById('h5p-iframe-' + contentId) as HTMLIFrameElement)
      : null;
    if (!frame) {
      frame = document.querySelector('iframe[id^="h5p-iframe-"]');
    }
    const H5PObj: any = (frame && (frame as any).contentWindow && (frame as any).contentWindow.H5P)
      ? (frame as any).contentWindow.H5P
      : (window as any).H5P;           // chế độ div
    const ds: any[] = (H5PObj && H5PObj.instances) || [];
    if (!ds.length) { return null; }
    if (contentId != null) {
      // H5P đổi contentId sang number nên dùng == chứ không ===
      // eslint-disable-next-line eqeqeq
      const found = ds.find((x) => x.contentId == contentId);
      if (found) { return found; }
    }
    return ds[0];
  } catch (e) {
    return null;
  }
}

/**
 * Đếm tương tác đã làm / tổng số.
 *
 * H5P không có API chung cho việc này, nên phải đi từ cụ thể tới tổng quát.
 * Quan trọng là KHÔNG dừng ở `getAnswerGiven()` quá sớm: nó chỉ trả lời "đã
 * làm hay chưa", nên bài 5 câu cũng bị đếm thành 1, và LMS báo nhầm
 * "còn 1 câu chưa làm".
 */
function demMotInstance(inst: any): { tong: number; daLam: number } {
  const khong = { tong: 0, daLam: 0 };
  if (!inst) { return khong; }

  try {
    // 1. InteractiveBook - đúng nguồn của ô "Interactions progress"
    if (Array.isArray(inst.chapters) && inst.chapters.length) {
      let tong = 0;
      let conLai = 0;
      inst.chapters.forEach((c: any) => {
        if (c.isSummary) { return; }
        tong += (c.maxTasks || 0);
        conLai += (c.tasksLeft || 0);
      });
      if (tong > 0) { return { tong, daLam: Math.max(tong - conLai, 0) }; }
    }

    // 2. SingleChoiceSet - biết chính xác đã qua bao nhiêu câu
    if (inst.options && Array.isArray(inst.options.choices) && inst.results) {
      const tong = inst.options.choices.length;
      const daLam = Math.min(
        (inst.results.corrects || 0) + (inst.results.wrongs || 0), tong);
      if (tong > 0) { return { tong, daLam }; }
    }

    // 3. QuestionSet - hỏi từng câu con
    if (Array.isArray(inst.questionInstances) && inst.questionInstances.length) {
      const ds = inst.questionInstances;
      const daLam = ds.filter((q: any) =>
        typeof q.getAnswerGiven === 'function' && q.getAnswerGiven()).length;
      return { tong: ds.length, daLam };
    }

    // 4. Vùng chứa có phơi con ra (H5P.Column) - cộng dồn từng con
    if (typeof inst.getInstances === 'function') {
      const con = inst.getInstances() || [];
      let tong = 0;
      let daLam = 0;
      con.forEach((c: any) => {
        const r = demMotInstance(c);
        tong += r.tong;
        daLam += r.daLam;
      });
      if (tong > 0) { return { tong, daLam }; }
    }

    // 5. Bài lá theo hợp đồng question type
    if (typeof inst.getAnswerGiven === 'function' &&
        typeof inst.getMaxScore === 'function' && inst.getMaxScore() > 0) {
      return { tong: 1, daLam: inst.getAnswerGiven() ? 1 : 0 };
    }
  } catch (e) { /* bỏ qua, trả 0 */ }

  return khong;
}

function demTuongTac(inst: any): { tong: number | null; daLam: number | null } {
  const r = demMotInstance(inst);
  return r.tong > 0 ? r : { tong: null, daLam: null };
}

function layDiemHienTai(layInstance?: () => any, contentId?: string | number): DiemH5P {
  const inst = timInstance(layInstance, contentId);
  if (!inst) {
    return { key: 'h5p-score', ok: false, lyDo: 'chua-tai-xong-noi-dung' };
  }
  if (typeof inst.getScore !== 'function' || typeof inst.getMaxScore !== 'function') {
    return { key: 'h5p-score', ok: false, lyDo: 'noi-dung-khong-co-diem' };
  }

  let score: any;
  let maxScore: any;
  try {
    score = inst.getScore();
    maxScore = inst.getMaxScore();
  } catch (e) {
    return { key: 'h5p-score', ok: false, lyDo: 'loi-getScore' };
  }

  const tt = demTuongTac(inst);

  let xapi: any = null;
  try {
    if (typeof inst.getXAPIData === 'function') { xapi = inst.getXAPIData(); }
  } catch (e) { xapi = null; }

  return {
    key: 'h5p-score',
    ok: true,
    contentId: inst.contentId,
    library: inst.libraryInfo ? inst.libraryInfo.versionedNameNoSpaces : null,
    score: typeof score === 'number' ? score : 0,
    maxScore: typeof maxScore === 'number' ? maxScore : 0,
    tongTuongTac: tt.tong,
    daLamTuongTac: tt.daLam,
    xapi
  };
}

/**
 * Bật cầu nối. Trả về hàm gỡ listener — gọi trong componentWillUnmount.
 *
 * @param layInstance  hàm lấy instance H5P, thường là () => this.h5pPlayer.current?.h5pInstance
 * @param parentUrl    origin của LMS, lấy từ process.env.REACT_APP_PARENT_URL
 */
export function batCauNoiNopBai(layInstance?: () => any, parentUrl: string = '*'): () => void {
  const xuLy = (event: MessageEvent) => {
    // Chỉ nhận lệnh từ chính cửa sổ cha (LMS)
    if (event.source !== window.parent) { return; }
    const d: any = event.data;
    if (!d || d.type !== 'layDiemH5P') { return; }

    let payload: DiemH5P;
    try {
      payload = layDiemHienTai(layInstance, d.contentId);
    } catch (e) {
      payload = { key: 'h5p-score', ok: false, lyDo: 'loi-khong-xac-dinh' };
    }

    window.parent.postMessage(
      { type: 'callParentFunction', payload },
      parentUrl || '*'
    );
  };

  window.addEventListener('message', xuLy, false);
  return () => window.removeEventListener('message', xuLy, false);
}

export default batCauNoiNopBai;
